import { ComponentProps, PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import AddIcon from '@mui/icons-material/Add';
import LockResetIcon from '@mui/icons-material/LockReset';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import UpdateIcon from '@material-ui/icons/Update';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MemoryIcon from '@mui/icons-material/Memory';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import SecurityIcon from '@mui/icons-material/Security';
import SmartToyIcon from '@mui/icons-material/SmartToy';

import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SearchModalProvider } from '@backstage/plugin-search';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { GlobalSearchShortcut } from './GlobalSearchShortcut';
import { SidebarSearchWithShortcut } from './SidebarSearchWithShortcut';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
  useSidebarPinState,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import CategoryIcon from '@material-ui/icons/Category';
import StorageIcon from '@material-ui/icons/Storage';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import RocketLaunch from '@mui/icons-material/RocketLaunch';
import LogoMake from './LogoMake';
import { getFlag } from '../../helpers/helpers';
import {
  AnalyticsContext,
  configApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useIsExternalUser } from '../../helpers/useIsExternalUser';
import { ExternalUserRouteGuard } from './ExternalUserRouteGuard';
import {
  useLegacySidebarNavigationAnalytics,
  useSidebarViewEvent,
} from './sidebar/sidebarAnalytics';
import { SidebarMore } from './SidebarMore';
import { KargoIcon } from './KargoIcon';
import {
  SidebarFlyoutProvider,
  SidebarItemWithSubmenu,
  SidebarSubmenuItem,
} from './SidebarFlyout';
import { BLUEPRINTS_TABS } from '../scaffolder/BlueprintsHeader';
import {
  CATALOG_PATHS,
  useCatalogTypeDestinations,
} from '../catalog/catalogNavigation';
import {
  DEVBOX_PATHS,
  DEVBOX_SUBMENU_ITEMS,
} from '@internal/backstage-plugin-devbox-manager';
import {
  VIBE_CODED_APPS_ROUTES,
  VIBE_CODED_APPS_SUBMENU_ITEMS,
} from '@internal/plugin-cloudflare-workers';
import {
  SOC2_REPORT_ROUTES,
  SOC2_REPORT_SUBMENU_ITEMS,
} from '@internal/plugin-soc2-report';
import {
  MAKE_SHELL_ROUTES,
  MAKE_SHELL_SUBMENU_ITEMS,
} from '@internal/plugin-make-shell';
import { DEPENDENCY_SECURITY_ROUTES } from '@internal/plugin-dependency-security';
import {
  MAKE_REVIEW_ROUTES,
  MAKE_REVIEW_SUBMENU_ITEMS,
} from '@internal/plugin-make-review';

const SIDEBAR_DRAWER_WIDTH_CLOSED = 72;
const SIDEBAR_DRAWER_WIDTH_OPEN = 200;
const DEFAULT_SIDEBAR_FAVORITES = [
  '/',
  '/catalog',
  '/docs',
  '/deployments',
  '/devbox-manager',
  '/vibe-coded-apps',
  '/kargo',
  '/create',
];

const BLUEPRINTS_SUBMENU_ITEMS: readonly SidebarSubmenuItem[] =
  BLUEPRINTS_TABS.slice(1).map(tab => ({
    title: tab.label,
    to: tab.path,
  }));

const CATALOG_ACTIONS: readonly SidebarSubmenuItem[] = [
  { title: 'Create Component', to: '/create' },
];

export const getSidebarContentStyle = (offset: number) => ({
  marginLeft: -offset,
  width: `calc(100% + ${offset}px)`,
  transition: 'margin-left 0.1s ease-out, width 0.1s ease-out',
});

export const getSidebarContentOffset = ({
  isMobile,
  isPinned,
}: {
  isMobile?: boolean;
  isPinned: boolean;
}) => {
  if (isMobile) {
    return 0;
  }

  return isPinned
    ? sidebarConfig.drawerWidthOpen - SIDEBAR_DRAWER_WIDTH_OPEN
    : sidebarConfig.drawerWidthClosed - SIDEBAR_DRAWER_WIDTH_CLOSED;
};

const useSidebarContentStyles = makeStyles({
  root: ({ offset }: { offset: number }) => getSidebarContentStyle(offset),
});

const SidebarContent = ({ children }: PropsWithChildren<{}>) => {
  const { isMobile, isPinned } = useSidebarPinState();
  const offset = getSidebarContentOffset({ isMobile, isPinned });
  const classes = useSidebarContentStyles({ offset });

  return <div className={classes.root}>{children}</div>;
};

const useSidebarLogoStyles = makeStyles({
  root: {
    width: SIDEBAR_DRAWER_WIDTH_CLOSED,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: SIDEBAR_DRAWER_WIDTH_CLOSED,
    marginLeft: 24,
  },
  linkContent: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: '16px',
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen, setOpen } = useSidebarOpenState();

  return (
    <div className={['tw-class', classes.root].join(' ')}>
      <button onClick={() => setOpen(!isOpen)} title="Expand/Collapse sidebar">
        {isOpen ? (
          <div className={classes.linkContent}>
            <LogoMake />
            <h1 className="dmo-ml-12 dmo-text-white dmo-text-xl dmo-font-bold">
              Backstage
            </h1>
          </div>
        ) : (
          <div className={classes.linkContent}>
            <LogoMake />
          </div>
        )}
      </button>
    </div>
  );
};

/**
 * Sends the same sidebar events as the BUI sidebar (sidebar/sidebarAnalytics),
 * so GA4 has a baseline to compare it with. `display: contents` keeps the
 * wrapper out of the layout; React click events from the sidebar's portalled
 * flyouts still bubble through it.
 */
const LegacySidebarAnalytics = ({ children }: PropsWithChildren<{}>) => {
  const { isPinned } = useSidebarPinState();
  const mode = isPinned ? 'expanded' : 'narrow';
  useSidebarViewEvent('legacy', mode);
  const onClick = useLegacySidebarNavigationAnalytics(mode);

  return (
    <div
      role="presentation"
      style={{ display: 'contents' }}
      onClickCapture={onClick}
    >
      {children}
    </div>
  );
};

type CatalogSidebarItemProps = ComponentProps<typeof SidebarItem> & {
  text: string;
  to: string;
};

const CatalogSidebarItem = (props: CatalogSidebarItemProps) => {
  const types = useCatalogTypeDestinations();

  if (types.length === 0) {
    return <SidebarItem {...props} />;
  }

  return (
    <SidebarItemWithSubmenu
      {...props}
      columns={[
        { ariaLabel: 'Browse', items: types },
        { ariaLabel: 'Actions', items: CATALOG_ACTIONS },
      ]}
    />
  );
};

/**
 * The original sidebar on core-components (Material UI) and the page around
 * it. Root uses it for people who turned on the `sidebar-legacy` flag, in
 * place of the BUI sidebar (sidebar/AppShell); external users always get it,
 * with a reduced menu.
 */
export const LegacyAppShell = ({ children }: PropsWithChildren<{}>) => {
  const config = useApi(configApiRef);
  const { isExternal } = useIsExternalUser();
  const isDeployments = getFlag(config, 'deployments');
  const isDevboxManager = getFlag(config, 'devbox-manager');
  const isKargo = getFlag(config, 'kargo');
  const isVibeCodedApps = getFlag(config, 'vibe-coded-apps');

  return (
    <SidebarPage>
      <LegacySidebarAnalytics>
        <Sidebar
          sidebarOptions={{
            drawerWidthClosed: SIDEBAR_DRAWER_WIDTH_CLOSED,
            drawerWidthOpen: SIDEBAR_DRAWER_WIDTH_OPEN,
          }}
        >
          <SidebarLogo />
          <ExternalUserRouteGuard />
          {isExternal ? (
            <SidebarGroup label="Menu" icon={<MenuIcon />}>
              <SidebarItem
                icon={() => <CloudQueueIcon />}
                to="cloudflare-workers"
                text="Internal Apps"
              />
              {/* Blueprints (`/create`) is intentionally not linked here for
            external users. The route stays reachable (via direct links and on
            the backend) — it's only hidden from this sidebar. */}
            </SidebarGroup>
          ) : (
            <>
              {/* GlobalSearchShortcut and the sidebar item share this modal state. */}
              <SearchModalProvider>
                <GlobalSearchShortcut />
                <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
                  {/* SearchContextProvider lives here so SearchModal's inner provider
                (inheritParentContextIfAvailable) inherits it, avoiding a
                post-mount setPageLimit side-effect that would fire a spurious
                empty-term query. Keep pageLimit aligned with Backstage's
                default so first-page-only re-ranking has enough candidates for
                exact quick-link matches such as Settings. */}
                  {/* searchSurface tags palette search analytics so they can be
                told apart from /search and TechDocs searches. */}
                  <AnalyticsContext attributes={{ searchSurface: 'palette' }}>
                    <SearchContextProvider
                      initialState={{
                        pageLimit: 25,
                        term: '',
                        types: [],
                        filters: {},
                      }}
                    >
                      <SidebarSearchWithShortcut />
                    </SearchContextProvider>
                  </AnalyticsContext>
                </SidebarGroup>
              </SearchModalProvider>
              <SidebarDivider />
              <SidebarGroup label="Menu" icon={<MenuIcon />}>
                <SidebarFlyoutProvider>
                  <SidebarMore defaultFavorites={DEFAULT_SIDEBAR_FAVORITES}>
                    {/* Global nav, not org-specific */}
                    <SidebarItem icon={() => <HomeIcon />} to="/" text="Home" />
                    <SidebarDivider />
                    <CatalogSidebarItem
                      icon={CategoryIcon}
                      to={CATALOG_PATHS.root}
                      text="Catalog"
                    />
                    <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
                    {isDeployments && (
                      <SidebarItem
                        icon={() => <RocketLaunch />}
                        to="deployments"
                        text="Deployments"
                      />
                    )}
                    {isDevboxManager && (
                      <SidebarItemWithSubmenu
                        icon={DeveloperBoardIcon}
                        to={DEVBOX_PATHS.root}
                        text="Devboxes"
                        items={DEVBOX_SUBMENU_ITEMS}
                      />
                    )}
                    {isVibeCodedApps && (
                      <SidebarItemWithSubmenu
                        icon={CloudQueueIcon}
                        to={VIBE_CODED_APPS_ROUTES.root.path}
                        text="Internal Apps"
                        items={VIBE_CODED_APPS_SUBMENU_ITEMS}
                      />
                    )}
                    {isKargo && (
                      <SidebarItem icon={KargoIcon} to="kargo" text="Kargo" />
                    )}
                    <SidebarItemWithSubmenu
                      icon={AddIcon}
                      to="/create"
                      text="Blueprints"
                      items={BLUEPRINTS_SUBMENU_ITEMS}
                    />
                    {/* End global nav */}
                    {getFlag(config, 'make-review') && (
                      <SidebarItemWithSubmenu
                        icon={RateReviewIcon}
                        to={MAKE_REVIEW_ROUTES.overview.path}
                        text="Make Review"
                        items={MAKE_REVIEW_SUBMENU_ITEMS}
                      />
                    )}
                    <SidebarItem
                      icon={AssessmentIcon}
                      to="ai-spend/reporting"
                      text="AI Spend"
                    />
                    <SidebarItem
                      icon={ExtensionIcon}
                      to="api-docs"
                      text="APIs"
                    />
                    {getFlag(config, 'cloud-agents') && (
                      <SidebarItem
                        icon={SmartToyIcon}
                        to="cloud-agents"
                        text="Cloud Agents"
                      />
                    )}
                    {getFlag(config, 'context-router') && (
                      <SidebarItem
                        icon={StorageIcon}
                        to="context-router"
                        text="Context Router"
                      />
                    )}
                    {getFlag(config, 'dependency-security') && (
                      <SidebarItem
                        icon={SecurityIcon}
                        to={DEPENDENCY_SECURITY_ROUTES.root.path}
                        text="Dependency Security"
                      />
                    )}
                    {getFlag(config, 'lock-manager') && (
                      <SidebarItem
                        icon={LockResetIcon}
                        to="lock-manager"
                        text="Locks"
                      />
                    )}
                    {getFlag(config, 'make-shell') && (
                      <SidebarItemWithSubmenu
                        icon={ViewModuleIcon}
                        to={MAKE_SHELL_ROUTES.root.path}
                        text="Make Shell"
                        items={MAKE_SHELL_SUBMENU_ITEMS}
                      />
                    )}
                    {getFlag(config, 'node-inventory') && (
                      <SidebarItem
                        icon={MemoryIcon}
                        to="node-inventory"
                        text="Node Inventory"
                      />
                    )}
                    {getFlag(config, 'soc2-report') && (
                      <SidebarItemWithSubmenu
                        icon={AssessmentIcon}
                        to={SOC2_REPORT_ROUTES.root.path}
                        text="SOC2 Report"
                        items={SOC2_REPORT_SUBMENU_ITEMS}
                      />
                    )}
                    {getFlag(config, 'versions-manager') && (
                      <SidebarItem
                        icon={UpdateIcon}
                        to="versions-manager"
                        text="Version Manager"
                      />
                    )}
                  </SidebarMore>
                </SidebarFlyoutProvider>
              </SidebarGroup>
            </>
          )}
          {isExternal && <SidebarSpace />}
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            <SidebarSettings />
          </SidebarGroup>
        </Sidebar>
      </LegacySidebarAnalytics>
      <SidebarContent>{children}</SidebarContent>
    </SidebarPage>
  );
};
